import {
  countdownConfigToSerializable,
  countdownIsoToLocalInputValue,
  countdownLocalInputValueToIso,
  createDefaultCountdownEditValues,
  formatCountdown,
  formatCountdownExactTime,
  isValidRecurringCountdownCron,
  normalizeCountdownConfig,
  normalizeCountdownDisplayUnit,
  normalizeCountdownType,
  stringifyCountdownConfig,
  type CountdownConfig,
  type CountdownEditRequest,
  type CountdownEditValues,
} from '#components/document-viewer/countdown/countdown-config';

export const NP_COUNTDOWN_TAG = 'np-countdown';
export const NP_COUNTDOWN_OPEN_TAG = `<${NP_COUNTDOWN_TAG}>`;
export const NP_COUNTDOWN_CLOSE_TAG = `</${NP_COUNTDOWN_TAG}>`;

export type CountdownBlockData = CountdownConfig;
export type MarkdownCountdownEditValues = CountdownEditValues;
export type MarkdownCountdownEditRequest = CountdownEditRequest;

export {
  createDefaultCountdownEditValues as createDefaultMarkdownCountdownEditValues,
  formatCountdown as formatMarkdownCountdown,
  formatCountdownExactTime as formatMarkdownCountdownExactTime,
  normalizeCountdownConfig as normalizeCountdownBlockData,
  normalizeCountdownDisplayUnit,
  normalizeCountdownType,
  stringifyCountdownConfig as stringifyMarkdownCountdownConfig,
  countdownIsoToLocalInputValue,
  countdownLocalInputValueToIso,
  isValidRecurringCountdownCron,
};

export type {
  CountdownDisplayUnit,
  CountdownType,
  FormattedCountdown,
} from '#components/document-viewer/countdown/countdown-config';

export interface ParsedMarkdownCountdownConfig {
  data: CountdownConfig;
  rawConfig: string;
  parseError: string | null;
}

function normalizeRawConfig(value: string): string {
  return value.replace(/\r\n/g, '\n').trim();
}

function minifyJsonConfig(value: unknown): string {
  return JSON.stringify(value);
}

export function parseMarkdownCountdownConfig(
  rawConfig: string,
): ParsedMarkdownCountdownConfig {
  const normalizedRaw = normalizeRawConfig(rawConfig);

  if (!normalizedRaw) {
    return {
      data: normalizeCountdownConfig({}),
      rawConfig: normalizedRaw,
      parseError: null,
    };
  }

  try {
    return {
      data: normalizeCountdownConfig(JSON.parse(normalizedRaw) as unknown),
      rawConfig: normalizedRaw,
      parseError: null,
    };
  } catch (error) {
    return {
      data: normalizeCountdownConfig({}),
      rawConfig: normalizedRaw,
      parseError:
        error instanceof Error
          ? error.message
          : 'Could not parse countdown JSON',
    };
  }
}

export function serializeMarkdownCountdownInnerConfig(params: {
  data: CountdownConfig;
  rawConfig?: string;
  parseError?: string | null;
}): string {
  return params.parseError && params.rawConfig?.trim()
    ? normalizeRawConfig(params.rawConfig)
    : minifyJsonConfig(countdownConfigToSerializable(params.data));
}

export function serializeMarkdownCountdownBlock(params: {
  data: CountdownConfig;
  rawConfig?: string;
  parseError?: string | null;
}): string {
  return `${NP_COUNTDOWN_OPEN_TAG}${serializeMarkdownCountdownInnerConfig(params)}${NP_COUNTDOWN_CLOSE_TAG}`;
}

export function createDefaultMarkdownCountdownValue(
  initialValues?: Partial<CountdownEditValues>,
): string {
  return serializeMarkdownCountdownBlock({
    data: normalizeCountdownConfig({
      type: initialValues?.type ?? 'target',
      name: initialValues?.name ?? '',
      targetAt: initialValues?.targetAt ?? '',
      cron: initialValues?.cron ?? '',
      displayUnit: initialValues?.displayUnit ?? 'auto',
    }),
  });
}
